'use client';

import { useState, useEffect, Fragment, useRef } from 'react';
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from '@headlessui/react';
import { Share2, Twitter, Facebook, Linkedin, Copy, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { shareToSocial } from '@/lib/utils/share';
import { analytics } from '@/lib/monitoring/analytics';
import { motion } from 'framer-motion';
import { siteConfig } from '@/constant/config';

interface ShareButtonProps {
  title: string;
  description: string;
  url: string;
  hashtags?: string[];
  className?: string;
  variant?: 'icon' | 'button';
  theme?: 'light' | 'dark';
  image?: string;
}

interface ShareOptionProps {
  onClick: () => void;
  icon: React.ElementType;
  label: string;
  className?: string;
}

const ShareOption = ({
  onClick,
  icon: Icon,
  label,
  className = 'text-gray-700',
}: ShareOptionProps) => (
  <motion.button
    whileHover={{ scale: 1.02, backgroundColor: 'rgb(243 244 246)' }}
    onClick={onClick}
    className={cn(
      'flex w-full items-center rounded-lg px-4 py-2.5 text-left text-sm transition-colors',
      className
    )}
  >
    <Icon className="mr-3 h-4 w-4" />
    {label}
  </motion.button>
);

export function ShareButton({
  title,
  description,
  url,
  hashtags = [],
  className,
  variant = 'icon',
  theme = 'light',
  image = `${siteConfig.url}/images/og.jpg`,
}: ShareButtonProps) {
  const [copied, setCopied] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const isMounted = useRef(true);

  // Ensure component only renders on client to prevent SSR issues
  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleShare = async (
    platform: 'twitter' | 'facebook' | 'linkedin' | 'native'
  ) => {
    try {
      await shareToSocial(platform, {
        title,
        text: description,
        url,
        hashtags,
        image,
      });
      analytics.trackUserAction('share_content', {
        platform,
        contentType: url.split('/')[1],
        contentId: url.split('/')[2],
      });
    } catch (error) {
      console.error('Error sharing:', error);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      analytics.trackUserAction('copy_link', {
        contentType: url.split('/')[1],
        contentId: url.split('/')[2],
      });
    } catch (error) {
      console.error('Error copying:', error);
    }
  };

  const buttonStyles = {
    icon: {
      light:
        'bg-gradient-to-r from-primary-500/10 to-accent-blue/10 text-primary-600 hover:from-primary-500/20 hover:to-accent-blue/20',
      dark: 'bg-white/20 text-white hover:bg-white/30',
    },
    button: {
      light: 'bg-gradient-to-r from-primary-500 to-accent-blue text-white',
      dark: 'bg-white text-primary-600',
    },
  };

  // Don't render HeadlessUI components during SSR
  if (!isClient) {
    return null;
  }

  return (
    <Popover className="relative">
      {({ open, close }) => (
        <>
          <PopoverButton
            onClick={(e) => {
              e.stopPropagation();
            }}
            className={cn(
              'inline-flex items-center justify-center rounded-lg px-3 py-2 text-sm font-medium shadow-sm backdrop-blur-sm transition-all duration-200',
              buttonStyles[variant][theme],
              'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
              className
            )}
          >
            <Share2 className={cn('h-4 w-4', variant === 'button' && 'mr-2')} />
            {variant === 'button' && 'Share'}
          </PopoverButton>

          <Transition
            as={Fragment}
            show={open}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <PopoverPanel className="absolute right-0 top-full z-40 mt-2 w-56 rounded-xl bg-white p-1 shadow-xl ring-1 ring-black ring-opacity-5 focus:outline-none">
              <div className="space-y-1">
                <ShareOption
                  onClick={() => {
                    handleShare('native');
                    close();
                  }}
                  icon={Share2}
                  label="Share"
                />
                <ShareOption
                  onClick={() => {
                    handleShare('twitter');
                    close();
                  }}
                  icon={Twitter}
                  label="Share on Twitter"
                />
                <ShareOption
                  onClick={() => {
                    handleShare('facebook');
                    close();
                  }}
                  icon={Facebook}
                  label="Share on Facebook"
                />
                <ShareOption
                  onClick={() => {
                    handleShare('linkedin');
                    close();
                  }}
                  icon={Linkedin}
                  label="Share on LinkedIn"
                />
              </div>
              <div className="my-1 h-px bg-gray-200" />
              <ShareOption
                onClick={() => {
                  handleCopy();
                  close();
                }}
                icon={copied ? Check : Copy}
                label={copied ? 'Copied!' : 'Copy Link'}
                className={copied ? 'text-green-600' : 'text-gray-700'}
              />
            </PopoverPanel>
          </Transition>
        </>
      )}
    </Popover>
  );
}
